import { Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormGroup, UntypedFormControl } from '@angular/forms';
import {
  loadJobDepartments,
  loadReferralCountByStage,
  loadReferralsByJobDepartment,
  loadReferralsByReferralDepartment,
  loadReferralsByJobFunction,
  loadReferralsByReferralFunction,
  referralReportsPageLoad,
  sendEmailReferralReport,
  changeStateReferralReport,
} from '@modules/reports/store/referral-reports.actions';
import {
  getReferralCountByStage,
  getReferralReportsPage,
  getReferralReportsPageIsLoaded,
  getReferralReportsPageIsLoading,
  getReferralOldestDate,
  getSendEmailSent,
} from '@modules/reports/store/referral-reports.selectors';
import { ActionCreator, select, Store } from '@ngrx/store';
import { PageTemplateTypes } from '@shared/page/page.component';
import { Pagination } from '@shared/pagination';
import { Observable, Subject } from 'rxjs';
import { Subpages } from '@modules/reports/models/subpages';
import { ReferralReportsFilters } from '@modules/reports/models/referral-reports-filters';
import { map, pairwise, startWith, takeUntil } from 'rxjs/operators';

import { FunnelChartElement } from 'mojo-funnel-chart';
import {
  allDepartments,
  allFunctions,
} from '@modules/reports/models/all-functions-and-departments';
import { SuccessAlert } from '@shared/alert';

enum FilterChangeTypes {
  STAGE = 'stage',
  REFERRAL_OPTION = 'referral-option',
  NONE_SELECTION = 'none',
  FILTER_CHANGE = 'filter-change',
}

@Component({
  selector: 'mojo-referral-reports',
  styleUrls: ['./referral-reports-list.container.scss'],
  templateUrl: './referral-reports-list.container.html',
})
export class ReferralReportsListContainerComponent implements OnDestroy, OnInit {
  templateType = PageTemplateTypes.twoColumns;
  defaultJobFunction = allFunctions;
  defaultJobDepartment = allDepartments;

  radioButtons = [
    { value: 0, label: 'Who made referrals?' },
    { value: 1, label: 'Who received referrals?' },
  ];

  jobFunction = new UntypedFormControl(this.defaultJobFunction);
  jobDepartment = new UntypedFormControl(this.defaultJobDepartment);
  referralFunction = new UntypedFormControl();
  referralDepartment = new UntypedFormControl();
  startDate = new UntypedFormControl(
    new Date(new Date().setDate(new Date().getDate() - 30)).toISOString(),
  );
  endDate = new UntypedFormControl(new Date().toISOString());
  stage = new UntypedFormControl();
  referralOption = new UntypedFormControl(1);

  filtersForm: UntypedFormGroup = new UntypedFormGroup({
    jobFunction: this.jobFunction,
    jobDepartment: this.jobDepartment,
    referralFunction: this.referralFunction,
    referralDepartment: this.referralDepartment,
    startDate: this.startDate,
    endDate: this.endDate,
    stage: this.stage,
    referralOption: this.referralOption,
  });

  referralReportsList$: Observable<Pagination> = this.store.select(getReferralReportsPage);

  isPageLoading$: Observable<boolean> = this.store.select(getReferralReportsPageIsLoading);
  isPageLoaded$: Observable<boolean> = this.store.select(getReferralReportsPageIsLoaded);
  referralCountsByStage$: Observable<FunnelChartElement[]> = this.store.pipe(
    select(getReferralCountByStage),
    map((countsByStage) => countsByStage.elements),
  );

  destroyed$: Subject<boolean> = new Subject();

  minDate$ = this.store.select(getReferralOldestDate);

  constructor(private store: Store<{}>) {
    this.filtersForm.valueChanges
      .pipe(
        startWith(this.filtersForm.value),
        pairwise(),
        takeUntil(this.destroyed$.asObservable()),
        this.getChangeType(),
      )
      .subscribe((change: FilterChangeTypes) => {
        const filters: ReferralReportsFilters = this.getActiveFilters();
        if (change === FilterChangeTypes.STAGE || change === FilterChangeTypes.REFERRAL_OPTION) {
          this.reloadContent(filters);
        } else if (change === FilterChangeTypes.NONE_SELECTION) {
          this.reloadContent(filters);
          this.store.dispatch(loadReferralCountByStage({ filters }));
        } else {
          this.reload(filters);
        }
      });
  }
  ngOnInit(): void {
    this.store.select(getSendEmailSent).subscribe((result) => {
      if (result) {
        this.store.dispatch(changeStateReferralReport({ flag: false }));
      }
    });
  }

  private getChangeType() {
    return map(([previousValue, currentValue]) => {
      if (
        (previousValue.jobFunction !== currentValue.jobFunction &&
          currentValue.jobFunction === 'No function') ||
        (previousValue.jobDepartment !== currentValue.jobDepartment &&
          currentValue.jobDepartment === 'No Department')
      ) {
        return FilterChangeTypes.NONE_SELECTION;
      } else if (previousValue.stage !== currentValue.stage) {
        return FilterChangeTypes.STAGE;
      } else if (previousValue.referralOption !== currentValue.referralOption) {
        return FilterChangeTypes.REFERRAL_OPTION;
      }
      return FilterChangeTypes.FILTER_CHANGE;
    });
  }

  reloadContent(filters: ReferralReportsFilters) {
    this.store.dispatch(referralReportsPageLoad({ filters }));
    this.getDepartmentChartAction().forEach((action) =>
      this.store.dispatch(action.call(this, { filters })),
    );
  }

  private getDepartmentChartAction(): ActionCreator[] {
    if (this.referralOption.value === 0) {
      return [loadReferralsByReferralDepartment, loadReferralsByReferralFunction];
    }
    return [loadReferralsByJobDepartment, loadReferralsByJobFunction];
  }

  reload(filters: ReferralReportsFilters) {
    this.store.dispatch(loadJobDepartments({ filters, departmentsPage: Subpages.REFERRAL_REPORT }));
    this.store.dispatch(loadReferralCountByStage({ filters }));
    this.reloadContent(filters);
  }

  changePage(page: number = 0) {
    this.store.dispatch(referralReportsPageLoad({ filters: { ...this.getActiveFilters(), page } }));
  }

  applyFilters(event) {
    const { selectedFunction: jobFunction, selectedDepartment: jobDepartment } = event;

    this.filtersForm.setValue({
      ...this.filtersForm.value,
      jobFunction,
      jobDepartment,
    });
  }

  applyDateFilter(range) {
    const { start, end } = range.value;

    this.filtersForm.setValue({
      ...this.filtersForm.value,
      startDate: !!start
        ? new Date(start.getTime() - range.value.start.getTimezoneOffset() * 60000).toISOString()
        : null,
      endDate: !!end
        ? new Date(
            end.getTime() - range.value.end.getTimezoneOffset() * 60000 + 60 * 60 * 24 * 1000,
          ).toISOString()
        : null,
    });
  }

  selectDepartment(jobDepartment: string) {
    const key = this.referralOption.value === 0 ? 'referralDepartments' : 'jobDepartments';
    const filters = { ...this.getActiveFilters(), [key]: this.getValueAsArray(jobDepartment) };
    this.reloadContent(filters);
  }

  selectFunction(jobFunction: string) {
    const key = this.referralOption.value === 0 ? 'referralFunctions' : 'jobFunctions';
    const filters = { ...this.getActiveFilters(), [key]: this.getValueAsArray(jobFunction) };
    this.reloadContent(filters);
  }

  getPercentage(index: number): Observable<number> {
    return this.referralCountsByStage$.pipe(
      map((countsByStage) => {
        if (
          countsByStage[index].total === 0 ||
          (index > 0 && countsByStage[index - 1].total === 0)
        ) {
          return 0;
        } else if (index === 0) {
          return 1;
        }
        return countsByStage[index].total / countsByStage[index - 1].total;
      }),
    );
  }

  private getActiveFilters(): ReferralReportsFilters {
    const baseFilters = {
      jobFunction: this.jobFunction.value,
      jobDepartment: this.jobDepartment.value,
      dateStart: this.startDate.value,
      dateEnd: this.endDate.value,
      startDate: this.startDate.value,
      endDate: this.endDate.value,
      stage: this.stage.value?.name,
    };

    return this.referralOption.value === 0
      ? {
          ...baseFilters,
          referralDepartments:
            this.defaultJobDepartment === this.jobDepartment.value
              ? null
              : this.getValueAsArray(this.jobDepartment.value),
          referralFunctions:
            this.defaultJobFunction === this.jobFunction.value
              ? null
              : this.getValueAsArray(this.jobFunction.value),
        }
      : {
          ...baseFilters,
          jobDepartments: this.getValueAsArray(this.jobDepartment.value, this.defaultJobDepartment),
          jobFunctions: this.getValueAsArray(this.jobFunction.value, this.defaultJobFunction),
        };
  }

  private getValueAsArray(value: string, defaultValue: string = null): string[] {
    return !value || value === defaultValue ? null : [value];
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  sendReferralReportSEmail() {
    this.store.dispatch(
      new SuccessAlert('Generating report… Once done, you will receive the report via email.'),
    );

    this.store.dispatch(sendEmailReferralReport({ params: this.getActiveFilters() }));
  }
}
